import { useState, useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { motion, AnimatePresence } from 'framer-motion'
import {
  PencilSquareIcon,
  MapPinIcon,
  CameraIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { saleSchema } from '../../schemas/saleSchema'
import LoadingSpinner from '../common/LoadingSpinner'
import ErrorMessage from '../common/ErrorMessage'
import FormField from '../common/FormField'
import FormSection from '../common/FormSection'
import SaleMap from './SaleMap'
import ImageManager from './ImageManager'
import Turnstile from '../common/Turnstile'

export default function SaleForm({
  mode = 'create',
  initialData = null,
  event = null,
  onSubmit,
  onCancel,
  loading = false,
  error = null,
  submitButtonText = 'Submit Sale',
  loadingText = 'Submitting...'
}) {
  const [primaryPhoto, setPrimaryPhoto] = useState(null)
  const [photos, setPhotos] = useState([])
  const [existingImages, setExistingImages] = useState([])
  const [primaryPhotoUrl, setPrimaryPhotoUrl] = useState(null)
  const [removedImages, setRemovedImages] = useState([])
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [turnstileError, setTurnstileError] = useState(null)

  const form = useForm({
    resolver: zodResolver(saleSchema),
    mode: 'onChange',
    defaultValues: {
      title: '',
      description: '',
      event_id: event?.id ? String(event.id) : '',
      location: {
        address: '',
        city: '',
        state: '',
        zip_code: '',
        latitude: '',
        longitude: ''
      }
    }
  })

  // Populate form when initial data loads (for edit mode)
  useEffect(() => {
    if (initialData) {
      const locationData = initialData.location ? {
        address: initialData.location.address || '',
        city: initialData.location.city || '',
        state: initialData.location.state || '',
        zip_code: initialData.location.zip_code || '',
        latitude: String(initialData.location.latitude) || '',
        longitude: String(initialData.location.longitude) || ''
      } : {
        address: '',
        city: '',
        state: '',
        zip_code: '',
        latitude: '',
        longitude: ''
      }

      const formData = {
        ...initialData,
        location: locationData
      }

      form.reset(formData)

      if (initialData.primary_photo_url) {
        setPrimaryPhotoUrl(initialData.primary_photo_url)
      }

      if (initialData.photo_urls && initialData.photo_urls.length > 0) {
        const additionalPhotos = initialData.photo_urls.filter(url => url !== initialData.primary_photo_url)
        setExistingImages(additionalPhotos)
      }
    }
  }, [initialData, form])

  const handleRemoveExistingImage = (imageUrl, imageId) => {
    setExistingImages(prev => prev.filter(url => url !== imageUrl))
    setRemovedImages(prev => [...prev, { url: imageUrl, id: imageId }])
  }

  const handleRemovePrimaryPhoto = () => {
    if (primaryPhotoUrl) {
      setRemovedImages(prev => [...prev, { url: primaryPhotoUrl, isPrimary: true }])
      setPrimaryPhotoUrl(null)
    }
    setPrimaryPhoto(null)
  }

  const handleSubmit = async (data) => {
    if (mode === 'create' && !turnstileToken) {
      setTurnstileError('Please complete the security verification')
      return
    }

    const formData = new FormData()

    Object.entries(data).forEach(([key, value]) => {
      if (key === 'location') {
        formData.append('sale[location][address]', value.address)
        formData.append('sale[location][city]', value.city || '')
        formData.append('sale[location][state]', value.state || '')
        formData.append('sale[location][zip_code]', value.zip_code || '')
        formData.append('sale[location][latitude]', value.latitude)
        formData.append('sale[location][longitude]', value.longitude)
      } else if (key !== 'photos' && key !== 'primary_photo') {
        formData.append(`sale[${key}]`, value)
      }
    })

    if (mode === 'create' && turnstileToken) {
      formData.append('sale[turnstile_token]', turnstileToken)
    }

    if (primaryPhoto) {
      formData.append('sale[primary_photo]', primaryPhoto)
    }

    if (photos && photos.length > 0) {
      photos.forEach((photo) => {
        formData.append('sale[photos][]', photo)
      })
    }

    if (mode === 'edit' && removedImages.length > 0) {
      removedImages.forEach((removed) => {
        if (removed.isPrimary) {
          formData.append('sale[remove_primary_photo]', 'true')
        } else {
          formData.append(`sale[remove_images][]`, removed.url)
        }
      })
    }

    try {
      await onSubmit(formData)
    } catch (err) {
      if (mode === 'create') {
        setTurnstileToken(null)
        setTurnstileError(null)
      }
      throw err
    }
  }

  const handleTurnstileVerify = (token) => {
    setTurnstileToken(token)
    setTurnstileError(null)
  }

  const handleTurnstileError = () => {
    setTurnstileToken(null)
    setTurnstileError('Security verification failed. Please try again.')
  }

  const handleTurnstileExpire = () => {
    setTurnstileToken(null)
    setTurnstileError('Security verification expired. Please verify again.')
  }

  const isSubmitDisabled = loading || !form.formState.isValid || (mode === 'create' && !turnstileToken)

  return (
    <div>
      {/* Error Banner */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="mb-6"
          >
            <div className="alert-danger">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Something went wrong</p>
                <p className="text-sm mt-0.5 opacity-90">{error.message}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-0">
          {/* Main Form Card */}
          <div className="card divide-y divide-border">
            {/* Section 1: Basic Details */}
            <div className="p-5 sm:p-8">
              <FormSection
                title="Sale Details"
                description="Tell people what you're selling"
                stepNumber={1}
              >
                <FormField
                  label="Title"
                  id="title"
                  name="title"
                  required
                  placeholder="e.g., Multi-family yard sale, Moving sale..."
                  helpText="A descriptive title helps people find your sale"
                />

                <FormField
                  label="Description"
                  id="description"
                  name="description"
                  type="textarea"
                  rows={4}
                  required
                  placeholder="Describe what you're selling — furniture, clothes, tools, etc."
                  helpText="Include types of items, condition, and any highlights"
                />
              </FormSection>
            </div>

            {/* Section 2: Location */}
            <div className="p-5 sm:p-8">
              <FormSection
                title="Location"
                description="Where will people find your sale?"
                stepNumber={2}
              >
                <div className="rounded-xl overflow-hidden border border-border shadow-xs">
                  <SaleMap
                    eventLocation={event?.location}
                    radius={event?.radius}
                  />
                </div>
                <AnimatePresence>
                  {form.formState.errors.location?.address && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 font-medium"
                    >
                      <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                      {form.formState.errors.location.address.message}
                    </motion.p>
                  )}
                </AnimatePresence>
              </FormSection>
            </div>

            {/* Section 3: Photos */}
            <div className="p-5 sm:p-8">
              <FormSection
                title="Photos"
                description="Add photos to attract more visitors"
                stepNumber={3}
              >
                <ImageManager
                  existingImages={mode === 'edit' ? existingImages : []}
                  primaryPhotoUrl={mode === 'edit' ? primaryPhotoUrl : null}
                  onPrimaryPhotoChange={setPrimaryPhoto}
                  onPhotosChange={setPhotos}
                  onRemoveExistingImage={mode === 'edit' ? handleRemoveExistingImage : undefined}
                  onRemovePrimaryPhoto={mode === 'edit' ? handleRemovePrimaryPhoto : undefined}
                />
              </FormSection>
            </div>

            {/* Section 4: Security (create only) */}
            {mode === 'create' && (
              <div className="p-5 sm:p-8">
                <FormSection
                  title="Verification"
                  description="Quick security check before submitting"
                  stepNumber={4}
                >
                  <Turnstile
                    onVerify={handleTurnstileVerify}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileExpire}
                  />
                  <AnimatePresence>
                    {turnstileError && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 font-medium"
                      >
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                        {turnstileError}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </FormSection>
              </div>
            )}
          </div>

          {/* Submit Area — sticky on mobile for easy thumb access */}
          <div className="sticky bottom-0 z-sticky mt-6 -mx-4 sm:mx-0 px-4 sm:px-0 py-4 sm:py-0
            bg-bg/95 backdrop-blur-sm sm:bg-transparent sm:backdrop-blur-none
            border-t border-border sm:border-0
            sm:static">
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              {mode === 'edit' && onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              )}

              <button
                type="submit"
                disabled={isSubmitDisabled}
                className={`btn-primary shadow-md hover:shadow-lg transition-shadow
                  ${mode === 'create' ? 'w-full sm:w-auto' : ''}`}
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    {loadingText}
                  </>
                ) : (
                  submitButtonText
                )}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
